import React, { useState } from 'react';
import axios from 'axios';
import { Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = window.__BACKEND_URL__;
const NEXT = { '': 'before', before: 'after', after: '' };

// "[Before] caption" style display name for lightbox captions
export const photoDisplayName = (p) => {
  const label = p.label === 'before' ? '[Before] ' : p.label === 'after' ? '[After] ' : '';
  return `${label}${p.caption || p.original_filename || 'Photo'}`;
};

export const PhotoLabelChip = ({ label, onClick, disabled, testid }) => {
  const styles = label === 'before'
    ? 'bg-sky-600 text-white'
    : label === 'after'
      ? 'bg-emerald-600 text-white'
      : 'bg-slate-700/80 text-slate-200 border border-dashed border-slate-500';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full transition-colors ${styles} ${onClick ? 'hover:opacity-85' : 'cursor-default'}`}
      title={onClick ? 'Tap to tag: Before → After → none' : undefined}
      data-testid={testid}
    >
      {label === 'before' ? 'Before' : label === 'after' ? 'After' : '+ Tag'}
    </button>
  );
};

/* Meta bar under a job photo: Before/After chip (tap to cycle) + caption
   with inline editing. Persists via PATCH /api/jobs/{jobId}/photos/{photoId}. */
const PhotoMetaBar = ({ jobId, photo, onUpdated, canEdit = true }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(photo.caption || '');
  const [saving, setSaving] = useState(false);

  const patch = async (payload) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.patch(
        `${API}/api/jobs/${jobId}/photos/${photo.id}`,
        payload,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      onUpdated?.(data.photo);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update photo');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const cycleLabel = () => {
    if (saving) return;
    patch({ label: NEXT[photo.label || ''] ?? 'before' });
  };

  const saveCaption = async () => {
    if (await patch({ caption: draft })) setEditing(false);
  };

  return (
    <div className="flex items-center gap-1.5 mt-1.5 min-w-0" data-testid={`photo-meta-${photo.id}`}>
      <PhotoLabelChip
        label={photo.label || ''}
        onClick={canEdit ? cycleLabel : undefined}
        disabled={saving}
        testid={`photo-label-chip-${photo.id}`}
      />
      {editing ? (
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') saveCaption(); if (e.key === 'Escape') setEditing(false); }}
            maxLength={200}
            placeholder="What does this photo show?"
            autoFocus
            className="flex-1 min-w-0 text-xs px-1.5 py-0.5 rounded border border-slate-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            data-testid={`photo-caption-input-${photo.id}`}
          />
          <button type="button" onClick={saveCaption} disabled={saving} className="text-emerald-500 hover:text-emerald-400" data-testid={`photo-caption-save-${photo.id}`}>
            <Check className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={() => { setEditing(false); setDraft(photo.caption || ''); }} className="text-slate-400 hover:text-slate-300">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <>
          <span className="flex-1 min-w-0 text-xs text-slate-400 truncate" data-testid={`photo-caption-text-${photo.id}`}>
            {photo.caption || (canEdit ? 'Add a note…' : '')}
          </span>
          {canEdit && (
            <button
              type="button"
              onClick={() => { setDraft(photo.caption || ''); setEditing(true); }}
              className="text-slate-400 hover:text-slate-200 flex-shrink-0"
              data-testid={`photo-caption-edit-${photo.id}`}
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default PhotoMetaBar;
